'use client';

import React, { useEffect, useState, useContext } from 'react';
import { UserContext } from '@/context/UserContext';
import FullReviewsModal from './FullReviewsModal';
import styles from './styles/ReviewsSection.module.css';
import ReviewAvatar from './reviewAvatar/ReviewAvatar';
import Link from 'next/link';

const DEFAULT_AVATAR = '/images/demo-profile.png';

function getReviewerName(review) {
  return review.userName || review.reviewerName || 'Anonymous';
}

function getReviewAvatar(review) {
  return review.avatarUrl || DEFAULT_AVATAR;
}

function getReviewProfileHref(review) {
  return review.profileHref || (review.userId ? `/public-profile/${review.userId}` : null);
}

function formatReviewDate(postedAt) {
  return postedAt ? new Date(postedAt).toLocaleDateString() : '';
}

export default function ReviewsSection({
  courseId,
  initialReviews = [],
  totalReviewCount = 0,
  canLeaveReview = false,
  existingReview = null,
}) {
  const user = useContext(UserContext);
  const [initialReviewsState, setInitialReviews] = useState(initialReviews);
  const [totalReviewCountState, setTotalReviewCount] = useState(totalReviewCount);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [canLeaveReviewState, setCanLeaveReview] = useState(canLeaveReview);
  const [reviewContent, setReviewContent] = useState(existingReview?.content || '');
  const [existingReviewState, setExistingReview] = useState(existingReview);
  const [editMode, setEditMode] = useState(!!existingReview);
  const [rating, setRating] = useState(existingReview?.rating || 0);

  // Character limit constants
  const MAX_CHAR_LIMIT = 800;
  const isOverLimit = reviewContent.length > MAX_CHAR_LIMIT;
  const isApproachingLimit = reviewContent.length > MAX_CHAR_LIMIT - 50;

  const loadInitialReviews = async () => {
    try {
      const res = await fetch(`/api/classes/${courseId}/reviews?limit=6`);
      if (!res.ok) throw new Error('Failed to load reviews');
      const data = await res.json();
      setInitialReviews(data.reviews || []);
      setTotalReviewCount(data.totalCount || 0);
    } catch {
      // do nothing
    }
  };

  const checkUserReviewStatus = async () => {
    if (!user) return;
    try {
      const checkRes = await fetch(`/api/classes/${courseId}/reviews/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkOnly: true }),
      });
      if (!checkRes.ok) {
        setCanLeaveReview(false);
      } else {
        const info = await checkRes.json();
        setCanLeaveReview(info.canReview);
        setExistingReview(info.existingReview);
        if (info.existingReview) {
          setReviewContent(info.existingReview.content);
          setRating(info.existingReview.rating || 0);
          setEditMode(true);
        }
      }
    } catch {
      setCanLeaveReview(false);
    }
  };

  const handleShowMoreReviews = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      alert('Please login first');
      return;
    }
    
    // Client-side validation for character limit
    if (reviewContent.length > MAX_CHAR_LIMIT) {
      alert(`Review must be ${MAX_CHAR_LIMIT} characters or less. Current length: ${reviewContent.length} characters.`);
      return;
    }
    
    const bodyToSend = { content: reviewContent, rating };
    try {
      let endpoint = `/api/classes/${courseId}/reviews`;
      let method = 'POST';
      if (editMode && existingReviewState) {
        endpoint = `/api/classes/${courseId}/reviews/${existingReviewState.id}`;
        method = 'PUT';
      }
      const r = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyToSend),
      });
      if (!r.ok) {
        const d = await r.json();
        alert(d.error || 'Failed to save review');
        return;
      }
      alert('Review saved successfully!');
      setReviewContent('');
      setRating(0);
      setEditMode(false);
      setExistingReview(null);
      loadInitialReviews();
      checkUserReviewStatus();
    } catch {
      // do nothing
    }
  };

  // Only fetch data that wasn't provided by server
  useEffect(() => {
    if (!courseId || !user || !user.id || canLeaveReview || existingReview) {
      return;
    }

    const syncUserReviewStatus = async () => {
      try {
        const checkRes = await fetch(`/api/classes/${courseId}/reviews/check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ checkOnly: true }),
        });
        if (!checkRes.ok) {
          setCanLeaveReview(false);
          return;
        }

        const info = await checkRes.json();
        setCanLeaveReview(info.canReview);
        setExistingReview(info.existingReview);
        if (info.existingReview) {
          setReviewContent(info.existingReview.content);
          setRating(info.existingReview.rating || 0);
          setEditMode(true);
        }
      } catch {
        setCanLeaveReview(false);
      }
    };

    syncUserReviewStatus();
  }, [canLeaveReview, courseId, existingReview, user]);

  return (
    <section className={styles.reviewsSection}>
      {/* <h2>Reviews</h2> */}

      {canLeaveReviewState && (
        <div className={styles.leaveReviewForm}>
          <h3>{editMode ? 'Edit Your Review' : 'Leave a Review'}</h3>
          <StarRatingInput rating={rating} setRating={setRating} />
          <form onSubmit={handleSubmitReview}>
            <textarea
              value={reviewContent}
              onChange={(e) => setReviewContent(e.target.value)}
              rows={4}
              maxLength={MAX_CHAR_LIMIT}
              required
            />
            <div className={styles.characterCounter}>
              <span 
                className={isOverLimit ? styles.overLimit : isApproachingLimit ? styles.approachingLimit : ''}
              >
                {reviewContent.length}/{MAX_CHAR_LIMIT} characters
              </span>
            </div>
            <button 
              type="submit" 
              className={styles.btnPrimary}
              disabled={isOverLimit}
            >
              {editMode ? 'Update' : 'Submit'}
            </button>
          </form>
        </div>
      )}

      <ReviewsGrid reviews={initialReviewsState} />

      {totalReviewCountState > 6 && (
        <button className={styles.btnPrimary} onClick={handleShowMoreReviews}>
          Show More Reviews
        </button>
      )}

      {isModalOpen && (
        <FullReviewsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          courseId={courseId}
        />
      )}
    </section>
  );
}

function ReviewsGrid({ reviews }) {
  return (
    <div className={styles.reviewsGrid}>
      {reviews.length > 0 ? reviews.map((rv) => (
        <TruncatedReviewCard
          key={rv.id}
          review={rv}
        />
      )) : (
        // <p> No current reviews yet. Stay tuned! </p>
        null
      )}
    </div>
  );
}

function TruncatedReviewCard({ review }) {
  const [expanded, setExpanded] = useState(false);
  const avatarPath = getReviewAvatar(review);
  const reviewerName = getReviewerName(review);
  const profileHref = getReviewProfileHref(review);
  const TRUNCATE_LENGTH = 120;
  const isLong = review.content.length > TRUNCATE_LENGTH;
  const contentToShow = expanded
    ? review.content
    : review.content.slice(0, TRUNCATE_LENGTH);

  return (
    <div className={styles.reviewItem}>
      {/* Top row has the star rating */}
      <div className={styles.ratingTop}>
        <StarRatingDisplay rating={review.rating} />
      </div>

      <blockquote>
        {contentToShow}
        {!expanded && isLong && <span>... </span>}
        {isLong && (
          <span
            onClick={() => setExpanded(!expanded)}
            className={styles.showRestToggle}
          >
            {expanded ? ' Show less' : ' Show rest'}
          </span>
        )}
      </blockquote>
      {/* Bottom row with avatar+name at bottom-left, date at bottom-right */}
      <div className={styles.cardFooter}>
        <div className={styles.footerLeft}>
          <ReviewAvatar storedImage={avatarPath} />
          {profileHref ? (
            <Link href={profileHref} className={styles.userName}>
              {reviewerName}
            </Link>
          ) : (
            <span className={styles.userName}>{reviewerName}</span>
          )}
        </div>
        <div className={styles.footerRight}>
          {formatReviewDate(review.postedAt)}
        </div>
      </div>
    </div>
  );
}

function StarRatingDisplay({ rating }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <span key={i} className={i <= rating ? styles.starFilled : styles.starEmpty}>
        ★
      </span>
    );
  }
  return <div className={styles.ratingStars}>{stars}</div>;
}

function StarRatingInput({ rating, setRating }) {
  return (
    <div className={styles.starRatingInput}>
      {Array.from({ length: 5 }, (_, idx) => {
        const starIndex = idx + 1;
        return (
          <span
            key={starIndex}
            className={starIndex <= rating ? styles.starFilled : styles.starEmpty}
            onClick={() => setRating(starIndex)}
          >
            ★
          </span>
        );
      })}
    </div>
  );
}
